import { useSignal, useSignalEffect } from "react-alien-signals";
import { useStore } from "zustand";
import throttle from "throttleit";
import {
  useRef,
  useCallback,
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  type CSSProperties,
  type KeyboardEvent,
  type ChangeEvent
} from "react";
import { useTranslation } from "react-i18next";
import { isToday } from "date-fns";
import { formatWithLocale } from "@/lib/utils/time";
import { createParser, type EventSourceMessage } from "eventsource-parser";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import Fingerprint2 from "fingerprintjs2";
import { Textarea } from "@/components/ui/textarea";
import { useIsPhone } from "@/hooks/use-mobile";
import {
  RefreshIcon,
  AddPicIcon,
  ArrowDownIcon,
  ShrinkIcon,
  SendUpIcon,
  TypingIcon,
  StopIcon,
  BrowserIcon,
  PhoneIcon,
  LoadingIcon,
  ExpandIcon,
  VoiceIcon,
  AITextIcon,
  StartsIcon,
  CloseIcon
} from "@/components/Icons";
import { DEFAULT_COLOR_THEME, WIDGET_NAME } from "@/constants";
import { getAgent, getAgentConfig } from "@/lib/api/client";
import { type ConversationMessage, KnowledgeType } from "@/lib/api/schema";

import {
  ChatMode,
  usePageStore,
  useConfigStore,
  conversationStore,
  tempConversationStore
} from "@/stores/page";
import { useDebounceFn } from "ahooks";
import LeadForm, { DEFAULT_FIELDS } from "./LeadForm";
import CallForm from "./CallForm";
import {
  cn,
  hexToOklab,
  getDarkerOklab,
  isInIframe,
  convertFieldNameToKey
} from "@/lib/utils";
import VoiceChatBox, { VoiceStatus } from "./VoiceChat";
import { initTrainingDrawerStore } from "@/stores/training";
import { GATE_API_HOST, GateWayHostmap } from "@/lib/api/routes";
import { FormFieldType } from "@/types/buildAgent";
import { type VoiceSwitchProps } from "@/components/VoiceAgentSetting";
import { isPossiblePhoneNumber } from "react-phone-number-input";

const LAST_CONVERSATION = "last_conversation";
const CHUNK_DISPLAY_INTERVAL = 30;
const CHUNK_DONE_DELAY = 1000;
const creditErrorMessage =
  "Your AI Agent has used up all available credits. Visitors can no longer chat with the AI. Please upgrade your plan or purchase additional credits to resume service.";

let formData: Record<string, any>;

let formFileData = new FormData();
let hasFormFile = false;

// ReactMarkdown components - defined outside component to prevent re-creation on each render
const createMarkdownComponents = (
  mode: ChatMode,
  message?: { error?: boolean },
  custom_fields?: any
) => ({
  blockquote: ({ ...props }: any) => (
    <blockquote
      {...props}
      className="px-2"
      style={{
        borderLeft: "4px solid var(--font-color)"
      }}
    />
  ),
  ol: ({ ...props }: any) => <ol {...props} className="list-decimal pl-4" />,
  ul: ({ ...props }: any) => <ul {...props} className="list-disc pl-4" />,
  img: ({ ...props }: any) => (
    <Zoom>
      <img
        {...props}
        className="h-auto max-w-full cursor-pointer rounded-lg"
        alt={props.alt || "Image"}
      />
    </Zoom>
  ),
  table: ({ ...props }: any) => (
    <div className="my-2 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
      <table
        {...props}
        className="min-w-full border-collapse bg-white text-sm"
      />
    </div>
  ),
  thead: ({ ...props }: any) => (
    <thead {...props} className="bg-gradient-to-r from-gray-50 to-gray-100" />
  ),
  tbody: ({ ...props }: any) => (
    <tbody {...props} className="divide-y divide-gray-100" />
  ),
  tr: ({ ...props }: any) => (
    <tr {...props} className="transition-colors hover:bg-gray-50/50" />
  ),
  th: ({ ...props }: any) => (
    <th
      {...props}
      className="border-b border-gray-200 px-6 py-3 text-left text-xs font-semibold tracking-wider text-gray-700 uppercase"
    />
  ),
  td: ({ ...props }: any) => (
    <td
      {...props}
      className="border-b border-gray-100 px-6 py-4 whitespace-nowrap text-gray-900"
    />
  ),
  a: ({ ...props }: any) => {
    // 检查是否是 book a demo 链接
    const isBookADemo = props.children === "book a demo";

    // 检查 custom_fields 中是否有匹配的 book_a_demo 链接
    const customFields = custom_fields || {};
    const bookADemoLinkKeys = Object.keys(customFields).filter(
      (key) =>
        key.startsWith("book_a_demo:") &&
        key.endsWith(":link") &&
        customFields[key] === props.href
    );
    const isCustomBookADemo = bookADemoLinkKeys.length > 0;

    // 获取自定义 title
    let customTitle = props.children;
    if (isCustomBookADemo && bookADemoLinkKeys.length > 0) {
      const leadType = bookADemoLinkKeys[0].split(":")[1]; // 获取 leadType
      const titleKey = `book_a_demo:${leadType}:title`;
      if (customFields[titleKey]) {
        customTitle = customFields[titleKey];
      }
    }

    // 如果链接匹配 custom_fields 中的 book_a_demo 链接，使用 book a demo 样式
    const shouldUseBookADemoStyle = isBookADemo || isCustomBookADemo;

    return (
      <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5",
          shouldUseBookADemoStyle
            ? "bg-primary ml-auto text-white capitalize"
            : message?.error
              ? "text-red-500"
              : "text-primary"
        )}
        href={props.href}
        data-testid={
          shouldUseBookADemoStyle ? "message-book-demo-link" : "message-link"
        }
      >
        <span
          className={cn(shouldUseBookADemoStyle ? "no-underline" : "underline")}
        >
          {customTitle}
        </span>
        <BrowserIcon
          className={cn("h-3.5 w-3.5", shouldUseBookADemoStyle && "hidden")}
        />
      </a>
    );
  }
});

// Simple components for disclaimer section
export const disclaimerMarkdownComponents = (
  isWidget: boolean,
  utm_source?: string
) => ({
  a: ({ ...props }: any) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className={cn("text-primary no-underline")}
      href={addUtmParams(
        props.href,
        isInIframe() ? utm_source : undefined,
        isWidget
      )}
      data-testid="message-power-link"
    >
      {props.children}
    </a>
  )
});

// Smart word segmentation function: decompose text into words, punctuation, and spaces
const splitIntoWords = (text: string): string[] => {
  if (!text) return [];

  const result: string[] = [];

  // Use more precise regex to split text
  // This regex matches: words, punctuation, spaces, Chinese characters, etc.
  const tokens =
    text.match(/[\w\u4e00-\u9fff]+|[^\w\u4e00-\u9fff\s]|\s+/g) || [];

  for (const token of tokens) {
    if (token.trim()) {
      // Non-whitespace characters, check if further splitting is needed
      const isChineseChar = /[\u4e00-\u9fff]/.test(token);

      if (isChineseChar && token.length > 1) {
        // Split Chinese characters individually
        for (const char of token) {
          result.push(char);
        }
      } else {
        // English words or individual Chinese characters
        result.push(token);
      }
    } else {
      // Preserve whitespace characters
      result.push(token);
    }
  }

  return result;
};

// Streaming message state management hook
const useStreamingMessage = () => {
  const streamStateRef = useRef({
    lastConversationId: "",
    chunks: [] as string[],
    isDone: false,
    chunkIndex: 1,
    chunkTimer: null as ReturnType<typeof setInterval> | null,
    chunkDoneTimer: null as ReturnType<typeof setTimeout> | null
  });

  const resetStreamState = useCallback(() => {
    const state = streamStateRef.current;
    state.chunks = [];
    state.isDone = false;
    state.chunkIndex = 1;
    if (state.chunkTimer) {
      clearInterval(state.chunkTimer);
      state.chunkTimer = null;
    }
    if (state.chunkDoneTimer) {
      clearTimeout(state.chunkDoneTimer);
      state.chunkDoneTimer = null;
    }
  }, []);

  // Clean up timers
  useEffect(() => {
    return () => {
      const state = streamStateRef.current;
      if (state.chunkTimer) clearInterval(state.chunkTimer);
      if (state.chunkDoneTimer) clearTimeout(state.chunkDoneTimer);
    };
  }, []);

  return {
    streamStateRef,
    resetStreamState
  };
};

// Helper function to add UTM parameters to URLs
const addUtmParams = (
  url: string | undefined,
  utm_source: string = "wati.io",
  isWidget: boolean
): string => {
  if (!url) return "";

  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set("utm_source", utm_source);
    urlObj.searchParams.set("utm_medium", isWidget ? "widget" : "spotlight");
    return urlObj.toString();
  } catch {
    // If URL parsing fails, return original URL
    return url;
  }
};

let externalUserId = "astra_test";

const FormMessageId = "form-conversation";

// Render text component with links
export const TextWithLinks = ({
  text,
  name,
  protocol
}: {
  text: string;
  name?: string;
  protocol?: boolean;
}) => {
  if (!text) return null;

  // Regex for matching URLs and domains
  // When protocol is true, strictly match http[s] protocol; when false, protocol is optional
  const urlRegex = protocol
    ? /https?:\/\/(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(?:\/[^\s<>|\\^`]*)?/g
    : /(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(?:\/[^\s<>|\\^`]*)?/g;

  const parts = text.split(urlRegex);
  const matches = text.match(urlRegex) || [];

  return (
    <>
      {parts.map((part, i) => (
        <span key={`part-${i}`} className="inline-flex max-w-full gap-1">
          {part}
          {matches[i] && (
            <a
              href={
                matches[i].startsWith("http")
                  ? matches[i]
                  : `https://${matches[i]}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-block max-w-full truncate hover:underline"
            >
              {name || matches[i]}
            </a>
          )}
        </span>
      ))}
    </>
  );
};

export type MessageEvent = {
  event: "message" | "message_end";
  answer: string;
  message_id: string;
  conversation_id: string;
  created_at: number;
};

export interface Message {
  sender: "user" | "agent";
  content: string;
  message_id: string;
  conversation_id: string;
  created_at: number;
  error?: boolean;
}

interface ChatChatProps {
  id: string;
  mode?: ChatMode;
  disable?: boolean;
  preview?: boolean;
  progress?: number;
  from_link?: string;
  clientIp?: string | null;
  country?: string | null;
  inVoice?: VoiceSwitchProps;
}

enum CallFormMode {
  Not = 0,
  On = 1,
  Done = 2
}

enum LeadFormSubmitMode {
  Not = 0,
  Submitted = 1,
  Cancelled = 2
}

export default function AgentChat({
  id,
  mode: agentMode = ChatMode.Widget,
  preview = false,
  disable = false,
  progress = 100,
  clientIp,
  country = "",
  from_link = "",
  inVoice
}: ChatChatProps) {
  const { t } = useTranslation("translations");
  const utm_source = from_link ? new URL(from_link).host : "wati.io";
  const pageStore = usePageStore();
  const agent = useStore(pageStore, (state) => state.agent);
  const voiceAgent = useStore(pageStore, (state) => state.voiceAgent);
  const setAgent = useStore(pageStore, (state) => state.setAgent);
  const configStore = useConfigStore();
  const appearance = useStore(
    configStore,
    (state) => state.brandKit.appearance_config
  );
  const brandKit = useStore(configStore, (state) => state.brandKit);
  const leadContactFields = useStore(
    configStore,
    (state) => state.config.lead_contact_fields
  );
  const formDisplayConfig = useStore(
    configStore,
    (state) => state.config.form_display_config
  );
  const setConfig = useStore(configStore, (state) => state.setConfig);
  const [state, setState] = useState({
    inputValue: "",
    quote: "",
    isLoading: false
  });
  const [showName, setShowName] = useState("");
  const [showMessageForm, setShowMessageForm] = useState<LeadFormSubmitMode>(
    LeadFormSubmitMode.Not
  );
  const [message, setMessage] = useState<
    ConversationMessage & { isLoading: boolean; quote?: string }
  >();
  const abortController = useRef<AbortController | null>(null);
  const showFormRef = useRef(true);
  // 跟踪表单显示状态
  const [formDisplayState, setFormDisplayState] = useState({
    attemptCount: 0,
    lastAttemptMessageCount: 0
  });
  const [callFormMode, setCallFormMode] = useState<CallFormMode>(
    CallFormMode.Not
  );
  const [isShrink, setIsShrink] = useState(false);

  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>(VoiceStatus.Stop);
  const [inFrameChat, setInFrameChat] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([]);

  const { streamStateRef, resetStreamState } = useStreamingMessage();

  const isMobile = useIsPhone();

  const isWidget = agentMode === ChatMode.Widget;

  const [conversation, setConversation] = useSignal(
    disable ? tempConversationStore : conversationStore
  );

  // 合并当前的 message 状态到 conversation.messages 中
  const allMessages = useMemo(() => {
    const messages = [...conversation.messages];

    // 如果有正在处理的 message，将其合并到消息列表中
    if (message) {
      // 检查是否已经存在相同的消息（避免重复）
      const existingIndex = messages.findIndex(
        (msg) =>
          msg.query === message.query &&
          Math.abs(msg.created_at - message.created_at) < 3 // 允许3秒的时间差
      );

      if (existingIndex >= 0) {
        // 更新现有消息的答案
        messages[existingIndex] = {
          ...messages[existingIndex],
          answer: message.answer,
          isLoading: true
        };
      } else {
        // 添加新的消息
        messages.push(message);
      }
    }

    return messages;
  }, [conversation.messages, message]);

  const messagesWithTimeGap = useMemo(() => {
    return allMessages.map((message, index) => {
      const nextMessage = allMessages[index + 1];
      if (nextMessage) {
        const timeDiff = nextMessage.created_at - message.created_at;
        return { ...message, lastDate: timeDiff };
      }
      return { ...message, lastDate: 0 };
    });
  }, [allMessages]);

  const isOnChat = useMemo(() => allMessages.length > 0, [allMessages]);

  // 计算是否应该显示 LeadForm
  const shouldShowLeadForm = useCallback(() => {
    if (!leadContactFields.use_form) return false;
    let {
      trigger_frequency = 0,
      maximum_attempts = 1,
      // eslint-disable-next-line prefer-const
      strategy = 1
    } = formDisplayConfig || {};
    if (strategy === 0) {
      trigger_frequency = 0;
      maximum_attempts = 1;
    }
    const currentMessageCount = allMessages.length;

    if (leadContactFields.closable) {
      // 如果 closable 为 true，根据 maximum_attempts 控制显示频率
      // 检查是否达到了触发频率
      if (
        (currentMessageCount + 1) % (trigger_frequency + 1) === 0 &&
        formDisplayState.attemptCount < maximum_attempts
      ) {
        return true;
      }
      return false;
    } else {
      // 如果 closable 为 false，根据 trigger_frequency 控制第几轮显示
      return currentMessageCount >= trigger_frequency;
    }
  }, [
    allMessages.length,
    leadContactFields,
    formDisplayConfig,
    formDisplayState
  ]);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatContentRef = useRef<HTMLDivElement>(null);

  const [, setTrainingDrawerStore] = useSignal(initTrainingDrawerStore);

  const appearanceConfig = appearance[agentMode];

  const theme = hexToOklab(
    appearanceConfig?.color_theme.primary_color ||
      appearanceConfig.color_theme.primary_color ||
      DEFAULT_COLOR_THEME
  );

  const themeLightBg = getDarkerOklab(theme, 0.2);
  // const isDarkTheme = isOklabDark(theme);

  const ConversationStarter = useMemo(
    () => appearanceConfig.conversation_starter.messages || [],
    [appearanceConfig]
  );

  const brandLogo = appearanceConfig.brand_logo.url || "/images/logo.svg";
  const brandName = appearanceConfig.name || agent?.name || "Untitled agent";

  const supportVoice = useMemo(
    () => voiceAgent?.id && voiceAgent?.is_enabled,
    [voiceAgent]
  );

  const isChatDisabled = useMemo(
    () => disable || state.inputValue.trim().length === 0,
    [disable, state]
  );

  const isVoiceDisabled = useMemo(
    () => disable || !supportVoice || state.isLoading,
    [disable, supportVoice, state]
  );

  // Memoize disclaimer markdown components to prevent re-creation
  const disclaimerComponents = useMemo(
    () => disclaimerMarkdownComponents(isWidget, utm_source),
    [isWidget, utm_source]
  );

  // Memoize welcome message to prevent re-creation
  const welcomeMessage = useMemo(
    () => ({
      sender: "agent" as const,
      content: appearanceConfig.welcome_message.message,
      message_id: "1",
      conversation_id: "",
      created_at: Math.floor(Date.now() / 1000)
    }),
    [appearanceConfig.welcome_message.message]
  );

  const showWelcomeMessage = useMemo(
    () =>
      appearanceConfig.welcome_message.popup_mode &&
      appearanceConfig.welcome_message.message &&
      callFormMode === CallFormMode.Not,
    [appearanceConfig, callFormMode]
  );

  const showConversationStarter = useMemo(
    () =>
      !isOnChat &&
      !state.isLoading &&
      appearanceConfig.conversation_starter.popup_mode &&
      ConversationStarter.length > 0 &&
      callFormMode === CallFormMode.Not,
    [
      isOnChat,
      state.isLoading,
      appearanceConfig,
      callFormMode,
      ConversationStarter
    ]
  );
  const leadFields = useMemo(
    () =>
      (leadContactFields?.fields || [])
        .filter((v) => v.type !== FormFieldType.Checkbox)
        .map((v) => {
          if (v.type === FormFieldType.Phone) return "phone";
          return convertFieldNameToKey(v.label);
        })
        .sort((a, b) => a.localeCompare(b))
        .join(","),
    [leadContactFields]
  );

  const createFingerprint = useCallback(() => {
    externalUserId = localStorage.getItem("external_user_id") || "";
    if (externalUserId) {
      return;
    }
    Fingerprint2.get((components) => {
      // When only callback function is provided, browser fingerprint is generated based on all configuration info
      const values = components.map((component) => component.value); // Array of configuration values
      const murmur = Fingerprint2.x64hash128(values.join(""), 31); // Generate browser fingerprint
      localStorage.setItem("external_user_id", murmur);
      externalUserId = murmur;
    });
  }, []);

  const createFingerprintLazy = useCallback(() => {
    // @ts-ignore
    if (window.requestIdleCallback) {
      requestIdleCallback(() => {
        createFingerprint();
      });
    } else {
      setTimeout(() => {
        createFingerprint();
      }, 500);
    }
  }, [createFingerprint]);

  useEffect(() => {
    setInFrameChat(isInIframe());
    if (!preview) {
      if (!agent) {
        getAgent(id).then((res) => {
          if (res && "agent_id" in res && res.agent_id) {
            setAgent(res);
          }
        });
        getAgentConfig(id).then((res) => {
          if (res.config) {
            setConfig(res.config);
          }
        });
      }
    }
    if (!preview) {
      streamStateRef.current.lastConversationId =
        localStorage.getItem(`${LAST_CONVERSATION}_${id}`) || "";
      const conversationList = localStorage.getItem(
        `${LAST_CONVERSATION}_${id}_list`
      );

      if (conversationList) {
        setConversation((prev) => ({
          ...prev,
          messages: JSON.parse(conversationList)
        }));
      }
    }
    createFingerprintLazy();
  }, [id, preview, agent, createFingerprintLazy]);

  const handleScrollToBottom = useCallback(() => {
    chatContentRef.current?.scrollTo({
      top: chatContentRef.current.scrollHeight,
      behavior: "smooth"
    });
    setShowScrollToBottom(false);
  }, []);

  const scrollToBottom = useMemo(
    () => throttle(() => handleScrollToBottom(), 300),
    []
  );

  useEffect(() => {
    const handleMessage = (event: globalThis.MessageEvent) => {
      if (event.data.type === "keyword") {
        setState((prev) => ({
          ...prev,
          quote: event.data.value,
          inputValue: "Tell me more about this"
        }));
      } else if (event.data.type === "open") {
        inputRef.current?.focus();
        scrollToBottom();
      } else if (event.data.type === "voice") {
        if (isVoiceDisabled) return;
        setVoiceStatus(VoiceStatus.Connecting);
      } else if (event.data.type === "message") {
        handleSendMessage(event.data.value);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const handleChatScroll = useCallback(
    throttle((e: Event) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const { scrollTop, scrollHeight, clientHeight } = target;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      // 如果接近底部，隐藏按钮；否则显示按钮
      setShowScrollToBottom(!isNearBottom && conversation.messages.length > 0);
    }, 100),
    [conversation.messages.length]
  );

  useEffect(() => {
    const chatContent = chatContentRef.current;
    if (!chatContent) return;

    chatContent.addEventListener("scroll", handleChatScroll);
    return () => {
      chatContent.removeEventListener("scroll", handleChatScroll);
    };
  }, [handleChatScroll]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setState((prev) => ({ ...prev, inputValue: e.target.value }));

      // Auto-resize textarea
      const textarea = e.target;
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 128) + "px";
    },
    []
  );

  const handleSendFile = () => {
    if (!hasFormFile) return;
    formFileData.append("agent_id", id);
    formFileData.append(
      "conversation_id",
      streamStateRef.current.lastConversationId
    );
    fetch("/api/conversation/v1/files", {
      method: "POST",
      body: formFileData
    });
    formFileData = new FormData();
    hasFormFile = false;
  };

  const updateMessage = useCallback(() => {
    const streamState = streamStateRef.current;
    if (streamState.chunkDoneTimer) {
      clearTimeout(streamState.chunkDoneTimer);
    }

    streamState.chunkDoneTimer = setTimeout(() => {
      if (!streamState.isDone) {
        updateMessage();
        return;
      }
      // Use functional updates to ensure getting latest message and state
      setMessage((currentMessage) => {
        if (currentMessage) {
          // Wait for next event loop before updating messages list
          setTimeout(() => {
            setState((prevState) => ({
              ...prevState,
              isLoading: false
            }));
            setConversation((prev) => ({
              ...prev,
              messages: [
                ...prev.messages,
                { ...currentMessage, isLoading: false }
              ]
            }));
            // Reset message
            setMessage(undefined);
          }, 100);
        }
        return currentMessage;
      });
      resetStreamState();
    }, CHUNK_DONE_DELAY);
  }, [resetStreamState]);

  const runThunkTimer = useCallback(() => {
    const streamState = streamStateRef.current;
    if (streamState.chunkTimer) return;

    streamState.chunkTimer = setInterval(() => {
      streamState.isDone = false;
      const text = streamState.chunks.slice(0, streamState.chunkIndex).join("");
      // Update message content
      setMessage((prev) => {
        if (!prev) return prev;
        return { ...prev, answer: text };
      });
      if (
        streamState.chunkIndex >= streamState.chunks.length &&
        streamState.chunkTimer
      ) {
        clearInterval(streamState.chunkTimer);
        streamState.chunkTimer = null;
        streamState.isDone = true;
        updateMessage();
        return;
      }
      streamState.chunkIndex += 1;
      scrollToBottom();
    }, CHUNK_DISPLAY_INTERVAL);
  }, [updateMessage, scrollToBottom]);

  const onEvent = useCallback(
    (ev: EventSourceMessage) => {
      const data = JSON.parse(ev.data);
      // console.log("onEvent data", data);

      if (ev.event === "message") {
        // @ts-ignore
        setMessage((prev) => ({
          ...prev,
          conversation_id: data.conversation_id as string,
          created_at: data.created_at as number
        }));

        // Smart word segmentation: decompose returned content into words and symbols
        const content = data.answer as string;
        const words = splitIntoWords(content);
        const streamState = streamStateRef.current;
        streamState.chunks = streamState.chunks.concat(words);
        // console.log("chunks", streamState.chunks);

        streamState.lastConversationId = data.conversation_id;
        runThunkTimer();
      }

      if (ev.event === "follow_up_questions") {
        setFollowUpQuestions((data.follow_up_questions as string[]) || []);
      }

      if (ev.event === "error") {
        setMessage((currentMessage) => {
          if (currentMessage) {
            setTimeout(() => {
              setState((prevState) => ({
                ...prevState,
                isLoading: false
              }));
              setConversation((prev) => ({
                ...prev,
                messages: [
                  ...prev.messages,
                  {
                    ...currentMessage,
                    answer: (data.error || data.answer) as string,
                    error: true
                  }
                ]
              }));
              setMessage(undefined);
            }, 100);
          }
          return currentMessage;
        });
        return;
      }

      if (ev.event === "message_end") {
        // Use functional updates to ensure getting latest message and state
      }
    },
    [runThunkTimer]
  );

  const cancelMessage = useCallback(() => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }

    setState((prev) => ({
      ...prev,
      isLoading: false
    }));

    setMessage((currentMessage) => {
      if (currentMessage) {
        // Wait for next event loop before updating messages list
        setTimeout(() => {
          setState((prevState) => ({
            ...prevState,
            isLoading: false
          }));
          setConversation((prev) => ({
            ...prev,
            messages: [
              ...prev.messages,
              { ...currentMessage, isLoading: false }
            ]
          }));
          setMessage(undefined);
        }, 100);
      }
      return currentMessage;
    });
  }, []);

  const handleRetry = useCallback(
    (originalContent: string) => {
      if (state.isLoading || disable) return;

      setState((prev) => ({
        ...prev,
        inputValue: originalContent,
        isLoading: true
      }));

      // Use a small delay to ensure state update
      setTimeout(() => {
        handleSendMessage(originalContent);
      }, 100);
    },
    [state.isLoading, disable]
  );

  const handleSendMessage = useCallback(
    async (overrideContent?: string, force = false) => {
      if (disable) return;
      let messageContent = overrideContent || state.inputValue.trim();

      if (!messageContent) return;
      setState((prev) => ({
        ...prev,
        isLoading: true
      }));

      try {
        formData = JSON.parse(
          localStorage.getItem("form_data") || ("{}" as string)
        );
      } catch (error) {
        console.error("Failed to parse form data:", error);
        formData = {};
      }
      const formValueKeys = Object.keys(formData)
        .sort((a, b) => a.localeCompare(b))
        .join(",");

      // console.log("formValueKeys=", formValueKeys);
      // console.log("leadFields=", leadFields);
      // console.log("shouldShowLeadForm=", shouldShowLeadForm());
      // 检查是否应该显示 LeadForm
      if (
        formValueKeys !== leadFields &&
        shouldShowLeadForm() &&
        !force &&
        !preview
      ) {
        showFormRef.current = true;
        // 更新尝试次数
        setFormDisplayState((prev) => ({
          ...prev,
          attemptCount: prev.attemptCount + 1,
          lastAttemptMessageCount: allMessages.length
        }));

        setMessage({
          query: messageContent,
          answer: "To better assist you, could you provide some basic details?",
          conversation_id: FormMessageId,
          isLoading: true,
          created_at: Math.floor(Date.now() / 1000)
        });
        return;
      }

      if (state.quote.trim().length > 0) {
        messageContent = "> " + state.quote.trim() + "\n\n" + messageContent;
      }

      // If there's an ongoing request, cancel it
      if (abortController.current) {
        abortController.current.abort();
      }

      // Create new AbortController
      const controller = new AbortController();
      abortController.current = controller;

      setState((prev) => ({
        ...prev,
        inputValue: "",
        quote: ""
      }));

      const streamState = streamStateRef.current;
      setMessage({
        query: messageContent,
        answer: "",
        conversation_id: streamState.lastConversationId,
        isLoading: true,
        created_at: Math.floor(Date.now() / 1000)
      });
      setTimeout(() => scrollToBottom(), 100);

      try {
        if (
          typeof formData["phone"] !== "undefined" &&
          !isPossiblePhoneNumber(formData["phone"])
        ) {
          delete formData["phone"];
        }
        const formValues: Record<string, any> = Object.keys(formData).reduce(
          (acc, key) => {
            if (DEFAULT_FIELDS.includes(key)) {
              acc[key] = formData[key];
            } else {
              acc.custom_fields[key] = formData[key];
            }
            return acc;
          },
          {
            custom_fields: {
              from_link: from_link || window.location.href,
              country
            }
          }
        );
        formValues.custom_fields = JSON.stringify(formValues.custom_fields);
        setShowName(formData?.first_name || "");

        postRunning(true);
        const host = GateWayHostmap[location.host] || GATE_API_HOST;
        const response = await fetch(
          preview ? `/api/conversation/v1/chat-preview` : `${host}/api/v1/chat`,
          {
            method: "POST",
            signal: controller.signal,
            body: JSON.stringify({
              agent_id: (formValues.agent_id as string) || id,
              external_user_id: externalUserId,
              tenant_id: window.tenantId || "test-tenant-123",
              message_content: messageContent,
              response_mode: "streaming",
              conversation_id: streamState.lastConversationId,
              source: agentMode,
              inputs: {
                first_name: "",
                last_name: "",
                email: "",
                ...formValues,
                conversation_id: streamState.lastConversationId
              }
            })
          }
        );

        if (!response.ok) {
          const json = await response.json();
          console.log("Response error:", json);
          const isCreditError = json.error.includes("Credit feature");
          setConversation((prev) => ({
            ...prev,
            messages: [
              ...prev.messages,
              {
                conversation_id: Date.now().toString(),
                query: messageContent,
                answer: isCreditError
                  ? creditErrorMessage
                  : `Sorry, there was an error: ${json.error}`,
                created_at: Math.floor(Date.now() / 1000),
                error: isCreditError ? false : true
              }
            ]
          }));

          setTimeout(() => {
            setState((prevState) => ({
              ...prevState,
              isLoading: false
            }));
            setMessage(undefined);
          }, 100);
          postRunning(false);
          return;
        }

        const parser = createParser({ onEvent });
        const decoder = new TextDecoder("utf-8");

        if (response.body) {
          if (response.body.getReader) {
            // Modern browsers with ReadableStream support
            const reader = response.body.getReader();
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              parser.feed(decoder.decode(value, { stream: true }));
            }
          } else {
            // Fallback for Safari
            const text = await response.text();
            parser.feed(text);
          }
        }
      } catch (error) {
        console.error("Error sending message:", error);

        if ((error as Error).name !== "AbortError") {
          setMessage(undefined);

          setState((prevState) => ({
            ...prevState,
            isLoading: false
          }));
        } else {
          // Request was cancelled by user
          setMessage(undefined);
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      } finally {
        abortController.current = null;
        if (!preview) {
          localStorage.setItem(
            `${LAST_CONVERSATION}_${id}`,
            streamState.lastConversationId
          );
          handleSendFile();
        }
        postRunning(true);
      }
    },
    [
      from_link,
      disable,
      state.inputValue,
      leadContactFields.fields,
      streamStateRef,
      scrollToBottom,
      preview,
      id,
      agentMode,
      onEvent,
      setConversation
    ]
  );

  useEffect(() => {
    scrollToBottom();
    if (!state.isLoading) {
      inputRef.current?.focus();
    }
  }, [state.isLoading, scrollToBottom]);

  useEffect(() => {
    setFollowUpQuestions([]);
  }, [id]);

  useLayoutEffect(() => {
    if (!preview) {
      window?.heap?.identify(clientIp || "");
      window?.heap?.addUserProperties({
        clientIp
      });
    }
  }, [clientIp, preview]);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const postRunning = useCallback((value: boolean) => {
    window.parent.postMessage(
      {
        type: WIDGET_NAME + "-running",
        value
      },
      "*"
    );
  }, []);

  const handleClose = useCallback(() => {
    if (disable) return;
    window.parent.postMessage(
      {
        type: WIDGET_NAME + "-close"
      },
      "*"
    );
  }, [disable]);

  const handleExpand = useCallback(() => {
    if (disable) return;
    window.parent.postMessage(
      {
        type: WIDGET_NAME + "-expand"
      },
      "*"
    );
  }, [disable]);

  const handlerClear = useCallback(() => {
    if (state.isLoading && message?.conversation_id !== FormMessageId) return;
    setConversation((prev) => ({
      ...prev,
      messages: []
    }));
    setMessage(undefined);
    setFollowUpQuestions([]);
    setState((prev) => ({
      ...prev,
      isLoading: false,
      inputValue: "",
      quote: ""
    }));
    streamStateRef.current.lastConversationId = "";
    if (!preview) {
      localStorage.removeItem(`${LAST_CONVERSATION}_${id}`);
      localStorage.removeItem(`${LAST_CONVERSATION}_${id}_list`);
    }
    setShowMessageForm(LeadFormSubmitMode.Not);
  }, [id, preview, state.isLoading, message?.conversation_id]);

  const { run: lazySaveConversationList } = useDebounceFn(
    () => {
      localStorage.setItem(
        `${LAST_CONVERSATION}_${id}_list`,
        JSON.stringify(conversation.messages)
      );
    },
    {
      wait: 1000
    }
  );

  useSignalEffect(() => {
    if (conversation.messages.length > 0 && !preview) {
      lazySaveConversationList();
    }
  });

  const handleImprove = useCallback((message: ConversationMessage) => {
    setTrainingDrawerStore((prev) => ({
      ...prev,
      open: true,
      trainingType: KnowledgeType.QA,
      isImprove: true,
      qaForm: {
        title: message.query,
        questions: [message.query],
        answer: message.answer
      }
    }));
  }, []);

  const specialSpotlightBox = inFrameChat && !isWidget && !isMobile && !preview;

  return (
    <div
      className={cn(
        "relative flex h-full w-full min-w-[300px] flex-1 flex-col",
        !preview && "overflow-hidden",
        isWidget &&
          "chat-running-box shadow-[0px_0px_27.799999237060547px_0px_rgba(0,0,0,0.12)]",
        !inFrameChat &&
          `${isWidget ? "mb-6 ml-auto" : "mx-auto"} max-w-[700px]`,
        state.isLoading && isWidget && "running border-none"
      )}
      style={
        {
          "--primary": theme,
          "--primary-light-bg": themeLightBg,
          "--border-width": "1.5px",
          "--border-radius": "12px",
          maxHeight: preview ? "calc(100vh - 112px)" : "100vh"
        } as CSSProperties
      }
      data-testid="agent-chat-container"
    >
      <div
        className={cn(
          "z-1 flex h-full flex-1 flex-col",
          isWidget && "bg-white"
        )}
        style={{
          borderRadius: isMobile ? "0" : "var(--border-radius)"
        }}
      >
        <div
          className={cn(
            "relative flex h-full flex-1 flex-col overflow-hidden",
            !isWidget &&
              "chat-running-box bg-white shadow-[0px_0px_27.799999237060547px_0px_rgba(0,0,0,0.12)]",
            specialSpotlightBox && "mx-9 mt-9",
            state.isLoading && !isWidget && "running"
          )}
          style={{
            scrollbarColor: "transparent transparent",
            scrollbarWidth: "none",
            borderRadius: isMobile ? "0" : "var(--border-radius)"
          }}
        >
          <div
            className="z-1 flex h-full flex-1 flex-col overflow-hidden bg-white"
            style={{
              borderRadius: "var(--border-radius)"
            }}
          >
            {/* Widget header */}
            <div
              className={cn(
                "z-1 flex h-16 items-center gap-3 rounded-t-xl border-b border-slate-200 px-5",
                disable ? "" : "justify-between"
              )}
              data-testid="chat-widget-header"
            >
              <span
                className="inline-flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-white"
                data-testid="chat-brand-logo"
              >
                <img
                  src={brandLogo}
                  alt="Astra Logo"
                  className="h-full w-full object-contain"
                />
              </span>
              <span
                className={cn("text-lg font-semibold text-[#403e3e]")}
                data-testid="chat-agent-name"
              >
                {brandName}
              </span>
              <RefreshIcon
                className={cn(
                  "ml-auto h-6 w-6 cursor-pointer text-[#403e3e]",
                  disable && "hidden"
                )}
                onClick={handlerClear}
                data-testid="chat-refresh-button"
              />
              <ArrowDownIcon
                className={cn(
                  "ml-2 h-6 w-6 cursor-pointer",
                  isWidget && !isMobile ? "hidden" : "text-[#403e3e]",
                  disable && "hidden"
                )}
                onClick={handleClose}
                data-testid="chat-close-button"
              />
              <ShrinkIcon
                className={cn(
                  "ml-2 h-6 w-6 cursor-pointer",
                  !isMobile && isWidget && isShrink
                    ? "text-[#403e3e]"
                    : "hidden",
                  disable && "hidden"
                )}
                onClick={() => {
                  setIsShrink((prev) => !prev);
                  handleExpand();
                }}
                data-testid="chat-close-button"
              />
              <ExpandIcon
                className={cn(
                  "ml-2 h-6 w-6 cursor-pointer",
                  !isMobile && isWidget && !isShrink
                    ? "text-[#403e3e]"
                    : "hidden",
                  disable && "hidden"
                )}
                onClick={() => {
                  setIsShrink((prev) => !prev);
                  handleExpand();
                }}
                data-testid="chat-close-button"
              />
            </div>

            {/* Chat content */}
            <div
              id="chat-content"
              className={cn(
                "relative z-1 flex max-h-full flex-1 flex-col gap-y-4 overflow-y-auto pt-6 pb-25",
                isWidget ? "px-5" : "px-4 sm:px-5",
                !isWidget && !isMobile ? "pb-0" : "pb-25",
                inVoice && "hidden"
              )}
              data-testid="chat-messages-container"
              ref={chatContentRef}
              style={{
                scrollbarColor: "transparent transparent",
                scrollbarWidth: "none"
              }}
            >
              {preview && !disable && (
                <p
                  className="text-center text-sm font-normal text-slate-400"
                  data-testid="chat-preview-notice"
                >
                  {t("agentChat.previewNotice")}
                </p>
              )}
              {showWelcomeMessage && (
                <AgentMessage
                  message={welcomeMessage}
                  mode={agentMode}
                  showName={showName}
                  isLoading={true}
                  onRetry={handleRetry}
                  logo={brandLogo}
                  agentName={brandName}
                  customFields={brandKit?.custom_fields}
                />
              )}
              {showConversationStarter && (
                <ol
                  className={cn(
                    "mt-auto flex shrink-0 flex-col items-end gap-3",
                    isWidget
                      ? "flex-wrap pb-2 pl-0"
                      : "mt-auto flex-nowrap px-1 pb-3"
                  )}
                >
                  {ConversationStarter.map((message, index) => (
                    <li
                      className="conversation-box relative inline-flex w-fit max-w-[80%] cursor-pointer items-center gap-1 rounded-xl shadow-md"
                      key={message}
                      onClick={() => handleRetry(message)}
                      data-testid={`conversation-starter-${index}`}
                    >
                      <span
                        className="max-w-full px-3 py-1.5 text-sm font-medium text-zinc-600"
                        title={message}
                      >
                        {message}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {messagesWithTimeGap.map((message, index) => (
                <MessageItem
                  key={index}
                  conversation={message}
                  mode={agentMode}
                  showName={showName}
                  showForm={
                    showMessageForm === LeadFormSubmitMode.Submitted &&
                    index === 0 &&
                    leadContactFields.use_form
                  }
                  onRetry={handleRetry}
                  logo={brandLogo}
                  agentName={brandName}
                  preview={preview}
                  isLoading={message.isLoading}
                  onImprove={() => handleImprove(message)}
                  customFields={brandKit?.custom_fields}
                />
              ))}

              {callFormMode === CallFormMode.On && (
                <>
                  <div className="inline-flex max-w-full flex-col items-start justify-start gap-2.5 overflow-hidden rounded-tr-xl rounded-br-xl rounded-bl-xl bg-slate-100 px-3 py-1.5 text-sm leading-tight font-normal text-black">
                    {t("agentChat.letsTalk")}
                  </div>
                  <CallForm
                    onClose={() => {
                      setCallFormMode(CallFormMode.Not);
                    }}
                    onOk={() => {
                      setCallFormMode(CallFormMode.Done);
                      setTimeout(() => setCallFormMode(CallFormMode.Not), 6000);
                    }}
                  />
                </>
              )}
              {callFormMode === CallFormMode.Done && (
                <>
                  <div
                    className="text-center text-sm text-slate-400"
                    data-testid="form-submitted-notice"
                  >
                    {t("agentChat.phoneSubmitted")}
                  </div>
                  <div className="inline-flex max-w-full flex-col items-start justify-start gap-2.5 overflow-hidden rounded-tr-xl rounded-br-xl rounded-bl-xl bg-slate-100 px-3 py-1.5 text-sm leading-tight font-normal text-black">
                    {t("agentChat.ringingHotline")} <br />
                    {t("agentChat.hangTight")}
                  </div>
                </>
              )}
              {followUpQuestions.length > 0 && !state.isLoading && (
                <ol
                  className={cn(
                    "mt-6 flex shrink-0 flex-col items-end gap-3",
                    isWidget
                      ? "flex-wrap pb-2 pl-0"
                      : "mt-auto flex-nowrap px-1 pb-3"
                  )}
                  data-testid="chat-followup-questions"
                >
                  {followUpQuestions.slice(0, 3).map((message, index) => (
                    <li
                      className="conversation-box relative inline-flex w-fit max-w-[80%] cursor-pointer items-center gap-1 rounded-xl shadow-md"
                      key={message}
                      onClick={() => handleRetry(message)}
                      data-testid={`followup-question-${index}`}
                    >
                      <span
                        className="max-w-full px-3 py-1.5 text-sm font-medium text-zinc-600"
                        title={message}
                      >
                        {message}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div
              className={cn(
                "mt-auto mb-2 text-center text-xs text-slate-400",
                appearanceConfig.disclaim_text.trim().length === 0 && "hidden"
              )}
              data-testid="chat-disclaimer"
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={disclaimerComponents}
              >
                {appearanceConfig.disclaim_text}
              </ReactMarkdown>
            </div>
            <div
              className={cn(
                "absolute right-1/2 left-1/2 z-2 ml-[-18px] inline-flex h-9 min-h-4 w-9 cursor-pointer items-center justify-center gap-2 rounded-full border border-zinc-200 bg-white shadow-[0px_4px_10px_0px_rgba(0,0,0,0.12)] transition-opacity duration-200",
                showScrollToBottom
                  ? "opacity-100"
                  : "pointer-events-none opacity-0",
                !isWidget && !isMobile ? "bottom-10" : "bottom-28"
              )}
              onClick={handleScrollToBottom}
            >
              <ArrowDownIcon className="h-4 w-4 text-[#5c5c5c]" />
            </div>

            {showFormRef.current &&
              message?.conversation_id === FormMessageId &&
              specialSpotlightBox && (
                <div className="absolute top-0 left-0 z-1 flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-[#e8e8e8]/70 backdrop-blur-[2px]">
                  <LeadForm
                    agentId={id}
                    requireId
                    required={!leadContactFields.closable}
                    onChange={(label, files: File[]) => {
                      hasFormFile = true;
                      formFileData = new FormData();
                      for (let i = 0; i < files.length; i++) {
                        formFileData.append(label, files[i]);
                      }
                    }}
                    onClose={(isSubmitted) => {
                      showFormRef.current = false;
                      setShowMessageForm(
                        isSubmitted
                          ? LeadFormSubmitMode.Submitted
                          : LeadFormSubmitMode.Cancelled
                      );

                      handleSendMessage(message.query, true);
                    }}
                  />
                </div>
              )}
          </div>
        </div>

        {/* Chat input */}
        <div
          className={cn(
            "z-2 mt-2.5",
            isWidget ? "absolute right-0 bottom-8 left-0 mx-4" : "",
            !isWidget && isMobile && "absolute right-0 bottom-8 left-0",
            voiceStatus !== VoiceStatus.Stop && "opacity-0"
          )}
          style={
            {
              "--border-width": "1.5px",
              "--border-radius": "12px"
            } as CSSProperties
          }
          data-testid="chat-input-container"
        >
          <div
            className={cn(
              "relative flex flex-col gap-1.5 bg-white px-4 py-2 shadow-[0px_0px_30px_-1px_rgba(0,0,0,0.10)]",
              isWidget
                ? "shadow-[0px_0px_30px_-1px_rgba(0,0,0,0.10)]"
                : "mx-auto w-[90%] max-w-[420px] min-w-[280px] md:w-[70%]",
              voiceStatus !== VoiceStatus.Stop && "hidden"
            )}
            id="chat-input-box"
            data-testid="chat-input-wrapper"
          >
            <div
              className={cn(
                "hidden",
                state.quote.trim().length > 0 &&
                  "z-1 inline-flex gap-2.5 overflow-hidden rounded-full bg-[#f2f2f2] px-3 py-[5px]"
              )}
            >
              <StartsIcon className="text-primary h-4 w-4 shrink-0" />
              <span
                className="flex-1 truncate text-sm font-normal text-[#5c5c5c]"
                title={state.quote}
              >
                {state.quote}
              </span>
              <CloseIcon
                className="h-4 w-4 cursor-pointer text-[#5c5c5c]"
                onClick={() => {
                  setState((prev) => ({
                    ...prev,
                    quote: "",
                    inputValue: ""
                  }));
                }}
              />
            </div>
            <Textarea
              className={cn(
                "z-1 max-h-16 min-h-0 flex-1 resize-none border-none p-0 text-sm text-black shadow-none focus-visible:ring-0",
                voiceStatus !== VoiceStatus.Stop && "hidden"
              )}
              style={{
                scrollbarColor: "transparent transparent",
                scrollbarWidth: "none"
              }}
              placeholder={
                state.isLoading
                  ? t("agentChat.replying")
                  : t("agentChat.enterMessage")
              }
              onKeyUp={handleKeyPress}
              value={state.inputValue}
              onChange={handleInputChange}
              disabled={disable || state.isLoading}
              ref={inputRef}
              data-testid="chat-message-input"
              rows={1}
            />
            <div className="z-1 flex shrink-0 items-center justify-end gap-4">
              <AddPicIcon
                className="hover:text-primary absolute left-2 hidden h-4 w-4 cursor-pointer text-slate-400"
                data-testid="chat-add-pic-button"
              />
              <PhoneIcon
                className={cn(
                  "text-primary hidden h-5 w-5 cursor-pointer",
                  (disable ||
                    voiceStatus !== VoiceStatus.Stop ||
                    state.isLoading) &&
                    "hidden"
                )}
                data-testid="chat-call-button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setCallFormMode(CallFormMode.On);
                  setTimeout(() => scrollToBottom(), 100);
                }}
              />
              <VoiceIcon
                className={cn(
                  "text-primary h-6 w-6 cursor-pointer",
                  isVoiceDisabled && "hidden cursor-not-allowed text-[#ececec]"
                )}
                onClick={() =>
                  !isVoiceDisabled && setVoiceStatus(VoiceStatus.Connecting)
                }
                data-testid="chat-send-button"
              />
              <SendUpIcon
                disabled={isChatDisabled}
                className={cn(
                  "text-primary h-6 w-6 cursor-pointer",
                  isChatDisabled && "cursor-not-allowed text-[#ececec]",
                  state.isLoading && "hidden"
                )}
                onClick={() => !isChatDisabled && handleSendMessage()}
                data-testid="chat-send-button"
              />
              <StopIcon
                className={cn(
                  "text-primary h-6 w-6 cursor-pointer",
                  state.isLoading ? "" : "hidden"
                )}
                onClick={cancelMessage}
                data-testid="chat-stop-button"
              />
            </div>

            <div
              className={cn(
                "absolute top-0 left-0 h-full w-full items-center gap-x-1.5 rounded-xl bg-[#eef4ff] px-4",
                progress > 90 ? "hidden" : "flex"
              )}
            >
              <LoadingIcon className="text-primary h-6 w-6" />
              <div className="text-base text-zinc-600">
                {t("agentChat.trainingWebsite", { progress })}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showFormRef.current &&
        message?.conversation_id === FormMessageId &&
        !specialSpotlightBox && (
          <div className="absolute top-0 left-0 z-1 flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-[#e8e8e8]/70 backdrop-blur-[2px]">
            <LeadForm
              agentId={id}
              requireId
              required={!leadContactFields.closable}
              onChange={(label, files: File[]) => {
                hasFormFile = true;
                formFileData = new FormData();
                for (let i = 0; i < files.length; i++) {
                  formFileData.append(label, files[i]);
                }
              }}
              onClose={(isSubmitted) => {
                showFormRef.current = false;
                setShowMessageForm(
                  isSubmitted
                    ? LeadFormSubmitMode.Submitted
                    : LeadFormSubmitMode.Cancelled
                );

                handleSendMessage(message.query, true);
              }}
            />
          </div>
        )}

      {((voiceAgent?.id && voiceStatus !== VoiceStatus.Stop) || inVoice) && (
        <VoiceChatBox
          id={id}
          mode={agentMode}
          isMobile={isMobile}
          preview={preview}
          maximum_session_time={voiceAgent?.maximum_session_time || 300}
          status={voiceStatus}
          setVoiceStatus={(state: VoiceStatus) => setVoiceStatus(state)}
          inVoice={inVoice}
          onReason={(reason: string) => {
            console.log("onReason: ", reason);
            const isCreditError = reason.includes("Credit feature");
            setConversation((prev) => ({
              ...prev,
              messages: [
                ...prev.messages,
                {
                  conversation_id: Date.now().toString(),
                  query: "",
                  answer: isCreditError ? creditErrorMessage : reason,
                  created_at: Math.floor(Date.now() / 1000),
                  error: isCreditError ? false : true
                }
              ]
            }));
          }}
        />
      )}
    </div>
  );
}

const AgentMessage = ({
  message,
  mode,
  // showName,
  logo,
  agentName,
  isLoading = false,
  customFields
  // onRetry
}: {
  message: Message;
  mode: ChatMode;
  showName?: string;
  isLoading?: boolean;
  logo?: string;
  agentName?: string;
  customFields?: any;
  onRetry: (content: string) => void;
}) => {
  // const isWidget = mode === ChatMode.Widget;

  // Memoize components to prevent re-creation on each render
  const markdownComponents = useMemo(
    () => createMarkdownComponents(mode, message, customFields),
    [mode, message, customFields]
  );

  return (
    <div
      className={cn(
        !!message.content || (!message.content && isLoading)
          ? "flex"
          : "hidden",
        message.sender === "user"
          ? "justify-end"
          : "flex-col justify-start rounded-[12px] rounded-tl-none bg-neutral-100"
      )}
      data-testid={`message-${message.sender}-${message.message_id}`}
      style={
        {
          "--font-color": message.sender === "agent" ? "#000" : "#fff"
        } as CSSProperties
      }
    >
      {message.sender === "agent" && (
        <div
          className="mr-2 flex shrink-0 items-center gap-1.5 px-4 pt-2"
          data-testid="agent-avatar"
        >
          <img src={logo} alt="Astra Logo" className="h-5 w-5 object-contain" />
          <span className="text-sm font-semibold text-black">{agentName}</span>
        </div>
      )}

      <div
        className={cn(
          "h-fit max-w-full px-4 py-2",
          message.sender === "user"
            ? "bg-primary text-white"
            : message.error
              ? "bg-red-50 text-red-700"
              : "text-black",
          message.sender === "user"
            ? "bg-primary rounded-[12px] rounded-tr-none"
            : ""
        )}
        data-testid={`message-bubble-${message.sender}`}
      >
        <div
          className="inline-flex max-w-full flex-col gap-y-2.5 text-sm"
          style={{ wordBreak: "break-word" }}
          data-testid="message-content"
        >
          {!message.content && (
            <TypingIcon
              style={{
                color: `var(--primary)`
              }}
              data-testid="typing-indicator"
            />
          )}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {message.content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export const MessageItem = ({
  conversation,
  mode,
  showForm,
  showName,
  logo,
  agentName,
  isLoading,
  preview,
  onRetry,
  onImprove = undefined,
  customFields,
  messageType = "text"
}: {
  conversation: ConversationMessage;
  mode: ChatMode;
  showForm: boolean;
  showName?: string;
  logo?: string;
  agentName?: string;
  isLoading?: boolean;
  preview?: boolean;
  onImprove?: () => void;
  onRetry: (content: string) => void;
  customFields?: any;
  messageType?: string;
}) => {
  const { t, i18n } = useTranslation("translations");
  // Memoize message objects to prevent re-creation on each render
  const userMessage = useMemo(
    () => ({
      sender: "user" as const,
      content: conversation.query,
      message_id: "1",
      conversation_id: conversation.conversation_id,
      created_at: conversation.created_at
    }),
    [conversation.query, conversation.conversation_id, conversation.created_at]
  );

  const agentMessage = useMemo(
    () => ({
      sender: "agent" as const,
      content: conversation.answer,
      message_id: "1",
      conversation_id: conversation.conversation_id,
      error: conversation.error,
      created_at: conversation.created_at
    }),
    [
      conversation.answer,
      conversation.conversation_id,
      conversation.error,
      conversation.created_at
    ]
  );

  return (
    <div className="flex flex-col gap-y-4">
      <AgentMessage
        message={userMessage}
        isLoading={isLoading}
        mode={mode}
        showName={showName}
        onRetry={onRetry}
        customFields={customFields}
      />
      {showForm && (
        <div
          className="text-center text-sm text-slate-400"
          data-testid="form-submitted-notice"
        >
          {t("agentChat.thanksForSharing")}
        </div>
      )}
      <AgentMessage
        message={agentMessage}
        mode={mode}
        showName={showName}
        logo={logo}
        isLoading={isLoading}
        agentName={agentName}
        onRetry={onRetry}
        customFields={customFields}
      />
      <div
        className={cn(
          onImprove && preview && !isLoading && messageType === "text"
            ? "flex cursor-pointer items-center gap-1.5 text-xs font-normal text-[#5c5c5c]"
            : "hidden"
        )}
        onClick={onImprove}
      >
        <AITextIcon className="h-3.5 w-3.5" />
        {t("agentChat.improveResponse")}
      </div>
      {!!conversation.lastDate &&
        conversation.lastDate > 60 * 60 &&
        !preview && (
          <div
            className="text-center text-xs text-slate-400"
            data-testid="message-timestamp"
          >
            {formatWithLocale(
              conversation.created_at * 1000,
              isToday(new Date(conversation.created_at * 1000))
                ? "HH:mm"
                : "dd/MM HH:mm",
              i18n.language
            )}
          </div>
        )}
    </div>
  );
};
